import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import * as fileSaver from 'file-saver';

import { CategorieDialogComponent } from './categorie-dialog/categorie-dialog.component';
import { MatConfirmDialogComponent } from 'src/app/shared/mat-confirm-dialog/mat-confirm-dialog.component';
import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import { ICategorie } from 'src/app/shared/models/categorie';
import { CategorieService } from './categorie-service';

@Component({
  selector: 'app-categorie-list',
  templateUrl: './categorie-list.component.html',
  styleUrls: ['./categorie-list.component.less']
})
export class CategorieListComponent implements OnInit, AfterViewInit {

  dataSource = new MatTableDataSource<ICategorie>();
  displayedColumns: string[] = ['nom', 'actions'];
  loading = false;

  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private categorieService: CategorieService,
    private dialog: MatDialog,
    private snackbarService: SnackbarService,
    private _liveAnnouncer: LiveAnnouncer,
    private router: Router
  ) {}

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.loadCategories();
  }

  goBack(): void {
    this.router.navigate(['/core/admin/centre-competence']);
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (item, property) => (item as any)[property];
  }

  announceSortChange(sortState: Sort): void {
    if (sortState.direction) {
      this._liveAnnouncer.announce(
        `Tri ${sortState.direction === 'asc' ? 'ascendant' : 'descendant'}`
      );
    } else {
      this._liveAnnouncer.announce('Tri réinitialisé');
    }
  }

  // ── Chargement ────────────────────────────────────────────────────────────

  loadCategories(): void {
    this.loading = true;
    this.categorieService.getAll().subscribe({
      next: (data) => {
        this.dataSource.data = data;
        this.loading = false;
      },
      error: (err) => {
        this.snackbarService.openSnackBar(
          this.extractErrorMessage(err, 'Erreur lors du chargement des catégories'),
          'error-snack-bar'
        );
        this.loading = false;
      }
    });
  }

  // ── Filtre ────────────────────────────────────────────────────────────────

  applyFilter(event: Event): void {
    this.dataSource.filter = (event.target as HTMLInputElement).value.trim().toLowerCase();
  }

  // ── Dialog ────────────────────────────────────────────────────────────────

  openDialog(categorie: ICategorie | null = null): void {
    const dialogRef = this.dialog.open(CategorieDialogComponent, {
      width: '500px',
      maxWidth: '95vw',
      data: categorie
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) this.loadCategories();
    });
  }

  // ── Suppression ───────────────────────────────────────────────────────────

  deleteCategorie(categorie: ICategorie): void {
    const dialogRef = this.dialog.open(MatConfirmDialogComponent, {
      width: '400px',
      data: { message: `Supprimer la catégorie "${categorie.nom}" ?` }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.categorieService.delete(categorie.id).subscribe({
        next: () => {
          this.snackbarService.openSnackBar('Catégorie supprimée avec succès');
          this.dataSource.data = this.dataSource.data.filter(c => c.id !== categorie.id);
        },
        error: (err) => {
          this.snackbarService.openSnackBar(
            this.extractErrorMessage(err, 'Erreur lors de la suppression'),
            'error-snack-bar'
          );
        }
      });
    });
  }

  // ── Export ────────────────────────────────────────────────────────────────

  exportData(): void {
    this.categorieService.exportData().subscribe({
      next: (data) => {
        if (!data) {
          this.snackbarService.openSnackBar('Aucune catégorie à exporter', 'error-snack-bar');
          return;
        }
        const blob = new Blob([data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        fileSaver.saveAs(
          blob,
          'Categories_' + new Date().toLocaleDateString().replace(/\//g, '-') + '.xlsx'
        );
        this.snackbarService.openSnackBar('Les catégories ont été exportées avec succès');
      },
      error: (err) => {
        this.snackbarService.openSnackBar(
          this.extractErrorMessage(err, 'Erreur lors de l\'export des catégories'),
          'error-snack-bar'
        );
      }
    });
  }

  // ── Helper erreurs ────────────────────────────────────────────────────────

  private extractErrorMessage(err: any, fallback: string): string {
    const body = err?.error;
    if (body?.message && typeof body.message === 'string') return body.message;
    if (typeof body === 'string') return body;
    return fallback;
  }
}